import { MessageCircle, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { cn } from "@/lib/utils"

interface Message {
  from: "agent" | "visitor"
  text: string
}

/* A short exchange that ends in a booking — the value, not a feature list. */
const SCRIPT: Message[] = [
  { from: "agent", text: "Hi! How can I help?" },
  { from: "visitor", text: "Do you have anything open on Thursday?" },
  {
    from: "agent",
    text: "We do. There's 2:00 or 4:30 PM. Want me to book you in?",
  },
  { from: "visitor", text: "4:30 works, thanks." },
  {
    from: "agent",
    text: "You're booked for Thursday 4:30 PM. A confirmation is on its way to your phone.",
  },
]

/* Phase 0 = launcher only; 1 = widget open; 2..6 = messages; 7 = booked chip. */
const FINAL_PHASE = SCRIPT.length + 2
/* First delay is the pause before the panel opens — kept a beat longer so
   the launcher registers before the widget takes over. */
const STEP_DELAYS = [900, 900, 1200, 1400, 1200, 1600, 900]

/**
 * The website-chat graphic: a chat panel with its launcher bubble beside it.
 * When scrolled into view the panel opens and the conversation plays out
 * once — it never replays, because the second viewing of an animation is a
 * distraction, not a demo.
 */
export function ChatDemo() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [started, setStarted] = useState(false)
  const [phase, setPhase] = useState(0)

  // Trigger once when roughly half the graphic is on screen.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          io.disconnect()
          if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setPhase(FINAL_PHASE)
          } else {
            setStarted(true)
          }
        }
      },
      { threshold: 0.5 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!started) return
    let at = 0
    const timers = STEP_DELAYS.map((delay, i) => {
      at += delay
      return setTimeout(() => setPhase(i + 1), at)
    })
    return () => timers.forEach(clearTimeout)
  }, [started])

  const open = phase >= 1
  const shown = Math.max(0, Math.min(phase - 1, SCRIPT.length))
  const upcoming = SCRIPT[shown]
  const typing = open && phase < FINAL_PHASE && upcoming?.from === "agent"
  const booked = phase >= FINAL_PHASE

  return (
    <div ref={rootRef} className="relative h-[400px]">
      {/* the visitor's website, skeletonised */}
      <div className="border-lp-fg/10 absolute inset-0 overflow-hidden rounded-2xl border bg-white p-5 shadow-[0_24px_60px_rgba(23,23,23,0.18)]">
        <div className="flex items-center justify-between">
          <div className="bg-lp-surface h-3 w-20 rounded-full" />
          <div className="flex gap-2">
            <div className="bg-lp-surface h-3 w-10 rounded-full" />
            <div className="bg-lp-surface h-3 w-10 rounded-full" />
            <div className="bg-lp-surface h-3 w-10 rounded-full" />
          </div>
        </div>
        <div className="bg-lp-surface mt-8 h-5 w-3/5 rounded-full" />
        <div className="bg-lp-surface mt-3 h-5 w-2/5 rounded-full" />
        <div className="bg-lp-surface/70 mt-5 h-3 w-4/5 rounded-full" />
        <div className="bg-lp-surface/70 mt-2 h-3 w-3/5 rounded-full" />
        <div className="mt-8 grid grid-cols-3 gap-3">
          <div className="bg-lp-surface/80 h-20 rounded-xl" />
          <div className="bg-lp-surface/80 h-20 rounded-xl" />
          <div className="bg-lp-surface/80 h-20 rounded-xl" />
        </div>
      </div>

      {/* widget, full height of the page, beside the launcher's corner */}
      <div
        className={cn(
          "border-lp-fg/10 absolute inset-y-4 right-20 flex w-[290px] max-w-[calc(100%-6rem)] origin-bottom-right flex-col overflow-hidden rounded-2xl border bg-white shadow-[0_24px_60px_rgba(23,23,23,0.25)] transition-all duration-400 ease-[cubic-bezier(0.16,1,0.3,1)]",
          open
            ? "scale-100 opacity-100"
            : "pointer-events-none scale-50 opacity-0",
        )}
      >
        <div className="border-lp-border/70 flex items-center gap-2 border-b px-4 py-3">
          <span
            className="size-2 rounded-full"
            style={{ background: "var(--lp-accent)" }}
          />
          <span className="text-lp-fg text-sm font-semibold">Chat with us</span>
          <X className="text-lp-muted/50 ml-auto size-3.5" />
        </div>

        <div className="flex flex-1 flex-col justify-end gap-2 overflow-hidden p-3">
          {SCRIPT.slice(0, shown).map((message, i) => (
            <div
              key={i}
              className={cn(
                "lp-msg-in max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-snug",
                message.from === "agent"
                  ? "bg-lp-surface text-lp-fg self-start rounded-bl-md"
                  : "lp-bubble-visitor text-lp-fg self-end rounded-br-md",
              )}
            >
              {message.text}
            </div>
          ))}

          {typing && (
            <div className="lp-msg-in bg-lp-surface flex gap-1 self-start rounded-2xl rounded-bl-md px-3 py-2.5">
              <span className="lp-dot bg-lp-muted/60 size-1.5 rounded-full" />
              <span
                className="lp-dot bg-lp-muted/60 size-1.5 rounded-full"
                style={{ animationDelay: "0.15s" }}
              />
              <span
                className="lp-dot bg-lp-muted/60 size-1.5 rounded-full"
                style={{ animationDelay: "0.3s" }}
              />
            </div>
          )}

          {booked && (
            <div className="lp-msg-in border-lp-border text-lp-fg self-center rounded-full border bg-white px-3 py-1.5 text-xs font-medium">
              Booked · Thursday 4:30 PM
            </div>
          )}
        </div>
      </div>

      {/* launcher — keeps its corner; the icon flips once the panel opens */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute right-4 bottom-4 grid size-13 place-items-center rounded-full text-white shadow-[0_10px_28px_rgba(23,23,23,0.28)]"
        style={{ background: "var(--lp-accent)" }}
      >
        {open ? <X className="size-5" /> : <MessageCircle className="size-6" />}
      </button>
    </div>
  )
}
